import { useMemo } from 'react'

import { Code } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import {
  convertBgMasterListItems,
  convertListBgMastersResponse,
  useListBgMasters,
} from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { DemandProcurementPlanMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/components/DemandProcurementPlanMasterFormFields'
import { useCreateDemandProcurementMaster } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/hooks/useCreateDemandProcurementMaster'
import { useGetDemandProcurementPlanMasterFormOptions } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/hooks/useGetDemandProcurementPlanMasterFormOptions'
import {
  type DemandProcurementMasterFormValues,
  demandProcurementMasterFormSchema,
} from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/models/schema'
import { resolveDemandProcurementMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/models/serverError'
import {
  convertListSubmitterMastersResponse,
  useListSubmitterMasters,
} from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useListSubmitterMasters'
import { useListTradingPartners } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/hooks/useListTradingPartners'

type Props = {
  isOpen: boolean
  onClose: () => void
}

/**
 * 需要調達計画マスタ 新規登録モーダル。
 *
 * bgMasterId は BG マスタのドロップダウンで選択する。
 * bgCode は選択した BG から自動取得する。
 * codes / bgs は後から Edit モーダルで追加する (Create 時は空でも可)。
 */
export const CreateDemandProcurementPlanMasterModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useCreateDemandProcurementMaster()
  // 集約 RPC: 小売/自己託送区分でフィルタ済みの bgs を取得 (区分 client filter 不要)
  const { data: formOptions, isPending: isLoadingFormOptions } =
    useGetDemandProcurementPlanMasterFormOptions()
  // codes section 用: 全件フェッチ (BG7 スコープ外、既存 hook 維持)
  const { data: allBgMastersData } = useListBgMasters()
  const { data: submitterData } = useListSubmitterMasters()
  const { data: tradingPartnersData } = useListTradingPartners()

  // 集約 RPC の bgs (小売/自己託送区分フィルタ済み): 代表BG select + 所属BG section 用
  // 代表BG は area narrow 不要 (代表BG が area を定義する側)
  const bgs = useMemo(
    () =>
      formOptions !== undefined
        ? convertBgMasterListItems(formOptions.bgs)
        : [],
    [formOptions],
  )
  // codes section 用: 全件 BG / submitter (BG7 スコープ外、既存 fetch 維持)。
  // list クエリ取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、
  // PR #5920 レビュー指摘)。
  const allBgMasters = useMemo(
    () =>
      allBgMastersData !== undefined
        ? convertListBgMastersResponse(allBgMastersData)
        : undefined,
    [allBgMastersData],
  )
  const submitters = useMemo(
    () =>
      submitterData !== undefined
        ? convertListSubmitterMastersResponse(submitterData)
        : undefined,
    [submitterData],
  )
  const tradingPartners = useMemo(
    () => tradingPartnersData?.items ?? [],
    [tradingPartnersData],
  )

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave: hookHandleSave,
    setValue,
    watch,
    setError,
    getValues,
    isDirty,
  } = useCrudModalForm<DemandProcurementMasterFormValues>({
    schema: demandProcurementMasterFormSchema,
    defaultValues: {
      bgMasterId: '',
      bgCode: '',
      note: '',
      codes: [],
      bgs: [],
      validFrom: todayCivilDate(),
      validTo: undefined,
    },
    onSubmit: async (values) => {
      await mutateAsync({
        bgMasterId: values.bgMasterId,
        bgCode: values.bgCode,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        // codes / bgs は fieldArray バインドで入力された値を送出する (RG6)
        // proc-plan: 代表BG を bgs[0] として自動挿入する (RG3, backend 不変条件)
        codes: values.codes.map((c) => ({
          code: c.code,
          codeName: c.codeName,
          isInternal: c.isInternal,
          validFrom: c.validFrom,
          validTo: c.validTo,
        })),
        // 需要調達計画マスタは 1 record = 1 BG (代表BG のみ) の設計 (spec
        // procurement-plan.md、 2026-08-05 UX指示: 所属BG UI は不要)。
        // backend は bgs[] を repeated で受けるが、 代表BG 1 件のみを常に送る。
        // 従来は PlanMasterBgsSection の locked 行が代表BG を保持していたが、
        // UI を除去したため submit 時に synth する。
        bgs: [{ bgMasterId: values.bgMasterId }],
        validFrom: values.validFrom,
        validTo: values.validTo,
      })
    },
    onClose,
    customErrorKeys: {
      [Code.Aborted]:
        'page:adminOcctoMaster.demandProcurementPlanMaster.serverError.aborted',
      [Code.InvalidArgument]:
        'page:adminOcctoMaster.demandProcurementPlanMaster.serverError.invalidArgument',
    },
    unknownErrorKey:
      'page:adminOcctoMaster.demandProcurementPlanMaster.serverError.unknown',
    // AC-OMC-004b: registry の BG_ALREADY_HAS_PROCUREMENT_MASTER entry は
    // spec の動的文言 (bgCode) を含む。submit 済み form 値 (values、N3
    // fresh 値契約) から必要な placeholder のみを明示合成して渡す (GSP の
    // CreateGenerationSalesPlanMasterModal と同形)。
    resolveDetailedError: (err, t, values) =>
      resolveDemandProcurementMasterDetailedError(err, t, {
        bgCode: values.bgCode,
      }),
  })

  // 選択した代表BG の areaCode を解決して Codes の候補フィルタに使う
  // (旧: 所属BG section の representative sync にも使っていたが section 削除で不要に)
  const selectedBgCode = watch('bgCode')
  const bgAreaCode = useMemo(() => {
    const code = selectedBgCode
    if (code === '' || code === undefined) return undefined
    return bgs.find((b) => b.bgCode === code)?.areaCode
  }, [selectedBgCode, bgs])

  // AC-02: 代表BG select の候補をレコードの適用開始日 (validFrom) 時点で
  // 絞り込む (4526-plan-master-valid-period-filter 設計書 §4)。
  const watchedRecordValidFrom = watch('validFrom')
  const bgCandidates = useMemo(
    () =>
      bgs.filter(
        (b) =>
          watchedRecordValidFrom === undefined ||
          isActiveOn(b.validFrom, b.validTo, watchedRecordValidFrom),
      ),
    [bgs, watchedRecordValidFrom],
  )

  // 期間外 BG の pre-check を hook の handleSave の前に挟む。
  const handleSave = (): void => {
    // 選択済み代表 BG が validFrom narrow 後の候補外なら submit を block
    // する (fallback option で UI 上は残るが、そのまま save すると失効 BG が
    // RPC 送信される silent invalid submission を防ぐ)。schema からは
    // allBgMasters にアクセスできないため Zod 手前で form 手動 validation。
    const currentBgMasterId = getValues('bgMasterId')
    if (
      currentBgMasterId !== '' &&
      !bgCandidates.some((b) => b.id === currentBgMasterId)
    ) {
      setError('bgMasterId', {
        message: t(
          'page:adminOcctoMaster.demandProcurementPlanMaster.form.bgMasterOutOfPeriod',
        ),
      })
      return
    }
    hookHandleSave()
  }

  // AC-01: codes[] 配列全体の min(1) 違反 (field-level 表示、
  // EsljAccountCodesEditor と同じ root/message フォールバックパターン)。
  const codesFieldError = errors.codes as
    | { root?: { message?: string }; message?: string }
    | undefined
  const codesErrorMessage =
    codesFieldError?.root?.message ??
    (typeof codesFieldError?.message === 'string'
      ? codesFieldError.message
      : undefined)

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      onClose={handleClose}
      serverError={serverError}
      // design (`design/admin/occto.html` #pm-edit-modal) は `.ecl-modal--lg`
      // (gen-plan / proc-plan は同 modal を label 切替で共有)。
      size="lg"
      title={t(
        'page:adminOcctoMaster.demandProcurementPlanMaster.modal.createTitle',
      )}
    >
      <DemandProcurementPlanMasterFormFields
        allBgMasters={allBgMasters}
        bgAreaCode={bgAreaCode}
        bgCandidates={bgCandidates}
        bgs={bgs}
        codesErrorMessage={codesErrorMessage}
        control={control}
        errors={errors}
        isEdit={false}
        isLoadingFormOptions={isLoadingFormOptions}
        selfBgCode={selectedBgCode}
        setValue={setValue}
        submitters={submitters}
        tradingPartners={tradingPartners}
      />
    </CrudModalLayout>
  )
}
